import { inject, observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "src/config";
import { User } from "src/models";
import { AuthStore, ItemStores, UserStore, QuestionStore, MeetingStore, SpaceStore } from "src/stores";
import { DataType, ItemType, UserType } from "src/types";
import { intersect, useClickOutside } from "src/util/util";
import styled from "styled-components";
import { Button, Icon, Text, IconId, Spacer } from ".";
import { Avatar } from "./avatar";
import { Badge } from "./badge";
import { Sidebar } from "./sidebar";
import { MeetingRoomUsers } from "./userSidebar";

export interface IFloatingMenuProps {
  type:        ItemType;
  meetingStore?: MeetingStore;
  itemStores?: ItemStores;
  questionStore?: QuestionStore;
  userStore?:  UserStore;
  authStore?:  AuthStore;
}

export const FloatingMenu = inject("itemStores", "userStore", "authStore", "questionStore", "meetingStore")(observer((props: IFloatingMenuProps) => {
  const { type, itemStores, authStore, userStore, questionStore, meetingStore } = props;
  const [isOpen, setIsOpen]                        = useState<boolean>(false);
  const toggleMenu                                 = React.useCallback(() => setIsOpen(!isOpen), [isOpen]);
  const [showSidebar,  setShowSidebar]             = React.useState<boolean>(false);
  const toggleSidebar                              = React.useCallback(() => setShowSidebar(!showSidebar), [showSidebar]);
  const wrapperRef                                 = useRef<HTMLDivElement>(null);
  const { t, i18n }                                = useTranslation();
  useClickOutside(wrapperRef, () => setIsOpen(false));

  const menuItems: {
    title:    string;
    type:     DataType[];
    icon:     IconId;
    disabled: boolean;
    hasBadge: boolean;
    onClick?: () => void;
  }[] = [
    {
      type:     [DataType.Deliverable],
      icon:     "watch",
      title:    t("deliverable"),
      disabled: false,
      hasBadge: true,
      onClick:  () => itemStores[DataType.Deliverable].setActive(itemStores[DataType.Deliverable].createNew()),
    },{
      type:     [DataType.Question],
      icon:     "questionAnswerIcon",
      title:    t("question"),
      disabled: false,
      hasBadge: true,
      onClick:  () => itemStores[DataType.Question].setActive(itemStores[DataType.Question].createNew()),
    },{
      type:     [DataType.Deliverable],
      icon:     "flag",
      title:    t("milestone"),
      disabled: !authStore.getCurUser.canEdit,
      hasBadge: true,
      onClick:  () => itemStores[DataType.Milestone].setActive(itemStores[DataType.Milestone].createNew()),
    },{
      type:     [DataType.Deliverable, DataType.Question, DataType.Meeting],
      title:    t("user_plural"),
      icon:     "people",
      disabled: false,
      hasBadge: false,
      onClick:  () => toggleSidebar(), 
    },{
      type:     [DataType.Deliverable, DataType.Question, DataType.Meeting],
      title:    t("chat"),
      icon:     "chat",
      disabled: true,
      hasBadge: false,
      onClick:  () => {},
    },{
      type:     [DataType.Meeting],
      title:    t("meeting"),
      icon:     "group",
      disabled: !authStore.getCurUser.canEdit,
      hasBadge: true,
      onClick:  () => itemStores[DataType.Meeting].setActive(itemStores[DataType.Meeting].createNew()), 
    }
  ];

  if(!menuItems.filter(i => i.type.includes(type)).length) return null;

  return (
    <Container>
      <div>
        <MainMenuButton variant="icon" onClick={() => toggleMenu()}>
          <Icon size="xlarge" color={theme.color.white} iconId="menu" />
        </MainMenuButton>
      </div>
      {isOpen && (
        <div ref = {wrapperRef}>
          {menuItems.map((i) => (
            <MenuContainer key={i.icon}>
              {i.type.includes(type) && (
                <MenuItem>
                  <StyledText size = "xsmall" color = "white">
                    {i.title}
                  </StyledText>
                  <MenuButton variant = "primary" disabled = {i.disabled} onClick={i.onClick}>
                    {i.hasBadge === true  ?
                            ( <Badge
                            className = "badge"
                            color   = {theme.color.buttons.primary.background}
                            border  = "1px"
                            content = {
                              <Icon
                                size   = "xsmall"
                                color  = {theme.color.white}
                                iconId = "add"
                              />
                            }
                          >
                      <Icon iconId = {i.icon} color = "white" size = "xlarge" />
                    </Badge>) : (
                      <Icon iconId = {i.icon} color = "white" size = "xlarge" />
                    )
                    }
                  </MenuButton>
                </MenuItem>
              )}
            </MenuContainer>
          ))}
        </div>
      )}
      <UserSidebar 
        closeOnClickOutside
        overlay
        content = {
          <MeetingRoomUsers />
        }  
        size    = "medium"
        anchor  = "right"
        open    = {showSidebar}
        onClose = {toggleSidebar}
        />
  
    </Container>
  );
}));

const UserSidebar = styled(Sidebar)`
  position:         absolute;
  z-index:          100;
  padding:          1rem 1rem;
  max-width:        10rem;
  overflow:         hidden;
`

const MenuButton = styled(Button)`
  background-color: ${theme.color.buttons.primary.background};
  padding:          1rem 1rem;
  border-radius:    50%;
  margin-left:      0.5rem;
  width:            4.8rem;
  height:           4.8rem;

  :hover .badge {
    background-color: ${theme.color.buttons.primary.hover};
  }

`;

const MainMenuButton = styled(Button)`
  position:         fixed;
  bottom:           2rem;
  right:            2rem;
  margin-top:       4rem;
  background-color: ${theme.color.buttons.primary.background};
  padding:          1rem 1rem;

  :hover {
    background-color: ${theme.color.buttons.primary.hover};
    box-shadow:       ${theme.boxShadow.hover};
  }

  @keyframes scale-in {
    from {
      transform: scale(0);
      opacity:   0;
    }
    to {
      transform: scale(1);
      opacity:   1;
    }
  }
`;

const MenuItem = styled.div`
  margin-top:      0.5rem;
  display:         flex;
  justify-content: flex-end;
  align-items:     center;

  :nth-of-type(3n) {
    animation: scale-in 0.2s;
  }

  :nth-of-type(2n) {
    animation: scale-in 0.4s;
  }

  :first-of-type {
    animation: scale-in 0.6s;
  }
`;

const StyledText = styled(Text)`
  background-color: ${theme.color.grayDefault};
  border-radius:    0.3rem;
  padding:          0.5rem 0.5rem;
`;

const MenuContainer = styled.div`
  bottom:         6rem;
  position:       relative;
  display:        flex;
  flex-direction: column;
`;

const Container = styled.div`
  bottom: 2rem;
  display: flex;
  flex-direction: row-reverse;
  position: fixed;
  right: 2rem;
  z-index: 2;
`;
